import React from "react";
import { useTranslation } from "react-i18next";
import {
  Mail,
  Phone,
  MessageSquare,
  Clock,
  Shield,
  HelpCircle,
} from "lucide-react";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import { brandForProductType } from "@core/lib/brand-config";

export const SupportPage: React.FC = () => {
  const { t } = useTranslation("forms");
  const brand = brandForProductType("form");
  return (
    <div className="flex flex-col min-h-screen-safe bg-[#030712] text-white">
      <ProductHeader productType="form" showAuth={true} />
      <div className="flex flex-1">
        <ProductSidebar productType="form" />
        <div className="flex-1">
          <div className="max-w-4xl mx-auto px-6 py-16">
            {/* Header */}
            <div className="text-center mb-16">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center mx-auto mb-6 border border-white/10">
                <HelpCircle className="w-8 h-8 text-purple-300" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {t("support.page.title")}
              </h1>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                {t("support.page.subtitle")}
              </p>
            </div>

            {/* Contact Info */}
            <div className="bg-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-8 mb-12">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <MessageSquare className="w-6 h-6 text-purple-400" />
                {t("support.contact.heading")}
              </h2>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">
                      {t("support.contact.email_label")}
                    </h3>
                    <p className="text-gray-400 mb-1">{brand.supportEmail}</p>
                    <p className="text-sm text-gray-500">
                      {/* Réponse sous 24h ouvrées */}
                    </p>
                  </div>
                </div>

                {/* Support prioritaire */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">
                      {t("support.contact.priority_title")}
                    </h3>
                    <p className="text-gray-400 text-sm">
                      {t("support.contact.priority_desc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Preview */}
            <div className="bg-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6">
                {t("support.faq.heading")}
              </h2>

              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
                  <h3 className="font-semibold mb-2">
                    {t("support.faq.create_poll_q")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("support.faq.create_poll_a")}
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
                  <h3 className="font-semibold mb-2">
                    {t("support.faq.data_secure_q")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("support.faq.data_secure_a")}
                  </p>
                </div>

                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
                  <h3 className="font-semibold mb-2">
                    {t("support.faq.export_q")}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {t("support.faq.export_a")}
                  </p>
                </div>
              </div>

              <div className="mt-6 text-center">
                <p className="text-gray-400">
                  {t("support.faq.more_help_prompt")}
                  <a
                    href={`mailto:${brand.supportEmail}`}
                    className="text-purple-400 hover:text-purple-300 ml-1"
                  >
                    {t("support.faq.contact_link")}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
